<script setup>
import {computed, ref} from 'vue';
import axios from 'axios';
import ErrorMessages from "../global/ErrorMessages.vue";
import SetupDomainModal from "./SetupDomainModal.vue";
import {axiosCatchHandler} from "../../modules/sharedGlobal";
import Swal from "sweetalert2";

const props = defineProps({
  whitelabel: {
    type: Object,
    required: true,
  },
  customDomain: {
    type: String,
    required: false,
    default: '',
  },
});

const whitelabel = ref(props.whitelabel);
const customDomain = ref(props.customDomain);
const loading = ref(false);
const errors = ref({});
const showHelpModal = ref(false);

const customDomainUnchanged = computed(() => {
  return customDomain.value === props.customDomain;
});

const customDomainSetup = computed(() => {
  return whitelabel.value.status === 'SETUP';
});

const customDomainError = computed(() => {
  return whitelabel.value.status === 'ERROR';
});

const customDomainPendingValidation = computed(() => {
  return whitelabel.value.status === 'PENDING' && whitelabel.value.cloudflare_id !== null;
});

const customDomainHasValidationErrors = computed(() => {
  return !!whitelabel.value.custom_domain_message;
});

const hasError = computed(() => {
  return customDomainError.value && customDomainHasValidationErrors.value;
});

const submit = async () => {
  loading.value = true;

  try {
    const { data } = await axios.post('/whitelabel/domain', {
      custom_domain: customDomain.value
    });

    reset();
    whitelabel.value = data.whitelabel;
  } catch (error) {
    loading.value = false;
    errors.value = error.response.data.errors;
    axiosCatchHandler(error, () => {});
  }
}

const reVerify = async () => {
  loading.value = true;

  try {
    const { data } = await axios.get('/whitelabel/setup/resubmit');

    reset();
    whitelabel.value = data.whitelabel;

    if (whitelabel.value.status === 'ERROR') {
      hasError.value = true;
    }
  } catch (error) {
    loading.value = false;
    errors.value = error.response.data.errors;
    axiosCatchHandler(error, () => {});

    if (error.response.status === 400) {
      await Swal.fire({
        icon: "warning",
        title: "Whoops!",
        text: error.response.data.error,
      });
    }
  }
}

const reset = () => {
  loading.value = false;
  hasError.value = false;
  errors.value = {};
}
</script>

<template>
  <div class="card">
    <div class="card-block">
      <div class="row">
        <div class="col">
          <p class="mrg-top-10 text-dark">
            <b>
              Custom Domain <br>
              <span v-if="customDomainSetup">
                <small>
                  Custom Domain setup, you can access the platform here:
                  <a
                    :href="`https://${whitelabel.custom_domain}`"
                    target="_blank"
                  >https://{{ whitelabel.custom_domain }}</a>
                </small>
              </span>
              <span v-else>
                <small>
                  Most customers choose a sub-domain on their agency website domain. For example, seo.yourdomain.com or platform.yourdomain.com.
                </small>
              </span>
            </b>
          </p>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-12 col-lg-6">
          <input
            v-model="customDomain"
            class="form-control"
            :class="{
              'error': hasError || errors.custom_domain
            }"
            :disabled="customDomainPendingValidation"
            placeholder="e.g dash.mywebsite.com"
            name="custom_domain"
            type="text"
          >
        </div>
        <div
          v-if="customDomainSetup"
          class="col-12 mt-4 col-lg-6 mt-lg-0 p-lg-0"
        >
          <div class="row align-items-center m-0">
            <div class="align-items-center d-flex pl-0">
              <button
                :disabled="loading || customDomainUnchanged"
                class="border-0 border-radius-6 btn-block btn-success button font-weight-bold h-100 mr-3 px-5 whitelabel-btn"
                @click="submit"
              >
                Update
              </button>
            </div>
            <div class="ps-4 text-success d-flex align-items-center">
              <svg-vue
                class="svg svg-32"
                icon="icons/tick-green"
                alt="tick"
              />
              <span class="font-weight-bold ml-1">Domain Verified</span>
            </div>
          </div>
        </div>
        <div
          v-else-if="customDomainPendingValidation"
          class="col-12 mt-4 col-lg-6 mt-lg-0 p-lg-0"
        >
          <div class="row align-items-center m-0">
            <div class="align-items-center d-flex pl-0">
              <button
                disabled
                class="border-0 border-radius-6 btn-block button font-weight-bold h-100 mr-3 px-5 full-opacity whitelabel-btn"
              >
                Update
              </button>
            </div>
            <div class="ps-4 icon yellow-text d-flex align-items-center">
              <svg-vue
                class="svg svg-24"
                icon="icons/yellow-circle"
                alt="tick"
              />
              <span class="mt-3 mt-md-0 font-weight-bold ml-2">We're checking your domain, please refresh this page after a couple of minutes to see if it was successful.</span>
            </div>
          </div>
        </div>
        <div
          v-else-if="customDomainError"
          class="col-12 mt-4 col-lg-6 mt-lg-0 p-lg-0"
        >
          <div class="row m-0 button-group">
            <button
              :disabled="loading || customDomainUnchanged"
              class="border-0 border-radius-6 button font-weight-bold btn-success px-5 whitelabel-btn"
              @click="submit"
            >
              Update
            </button>
            <button
              :disabled="loading"
              class="border-0 border-radius-6 button font-weight-bold btn-danger px-5 whitelabel-btn"
              @click="reVerify"
            >
              Re-Verify
            </button>
          </div>
        </div>
        <div
          v-else
          class="col-12 mt-4 col-lg-6 mt-lg-0 p-lg-0"
        >
          <button
            :disabled="loading"
            class="border-0 border-radius-6 button font-weight-bold label-info px-5 whitelabel-btn"
            @click="submit"
          >
            Submit
          </button>
        </div>
      </div>
      <div class="row">
        <div class="col-12 col-lg-6">
          <ErrorMessages
            v-if="errors?.custom_domain"
            :errors="errors.custom_domain"
          />

          <div class="info-block mt-3">
            <svg-vue
              class="svg svg-32"
              icon="icons/info-blue"
              alt="info"
            />
            <a
              class="pointer"
              @click="showHelpModal = true"
            >
              <u>Need help? Click here to read our configuration guide.</u>
            </a>
          </div>

          <SetupDomainModal
            v-if="showHelpModal"
            @close="showHelpModal = false"
          />
        </div>
        <div
          v-if="customDomainHasValidationErrors"
          class="col-12 mt-4 col-lg-6 mt-lg-0 p-lg-0"
        >
          <div class="alert-danger align-items-lg-start border-radius-6 d-flex p-3 text-danger mt-3 warning-alert">
            <svg-vue
              class="svg mr-2 svg-32"
              icon="icons/warning-red"
            />
            <span>
              {{ whitelabel.custom_domain_message }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.whitelabel-btn {
  max-width: 200px;
  width: 100%;
  padding-top: 10px !important;
  padding-bottom: 11px !important;
}

.warning-alert {
  max-width: 416px;
  width: 100%;
  border: 1px solid #D47777;
  background: #FCF0F0;
}

.button-group {
  display: flex;
  gap: 16px;
}

.yellow-text {
  color: #EAB64D;
}

.full-opacity {
  opacity: 1 !important;
}

.svg-32 {
  max-width: 32px !important;
}

.svg-24 {
  max-width: 24px !important;
}
</style>
